import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Option, Select, Spinner, Textarea } from '@material-tailwind/react';
import { ModalX } from '../../components';
import { NotifyError } from '../../utils/functions';
import { useAddBuilding, useUpdateBuilding } from '../../custom-hooks/use-buildings';

const INITIAL_FORM_DATA = {
  name: '',
  totalFloors: 1,
  buildingCode: '',
  address: '',
  constructionYear: '',
  architect: '',
  description: '',
  status: 'operational',
  fireSafetyStatus: false,
  legalClearance: false,
  emergencyExits: 0,
  totalCapacity: 0,
  internetAvailability: false,
  powerBackup: false,
  washrooms: 0,
  elevators: 0,
  parkingCapacity: 0,
};

const BuildingCrudModal = ({ isOpen, onClose, branchId, building = null, existingCount = 0 }) => {
  const isUpdating = !!building?._id;
  const addBuildingMutation = useAddBuilding();
  const updateBuildingMutation = useUpdateBuilding();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  useEffect(() => {
    if (!isOpen) return;
    if (building) {
      setFormData({
        ...INITIAL_FORM_DATA,
        name: building.name || '',
        totalFloors: building.totalFloors || 1,
        buildingCode: building.buildingCode || '',
        address: building.address || '',
        constructionYear: building.constructionYear || '',
        architect: building.architect || '',
        description: building.description || '',
        status: building.status || 'operational',
        fireSafetyStatus: !!building.fireSafetyStatus,
        legalClearance: !!building.legalClearance,
        emergencyExits: building.emergencyExits || 0,
        totalCapacity: building.totalCapacity || 0,
        internetAvailability: !!building.internetAvailability,
        powerBackup: !!building.powerBackup,
        washrooms: building.washrooms || 0,
        elevators: building.elevators || 0,
        parkingCapacity: building.parkingCapacity || 0,
      });
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
  }, [isOpen, building]);

  const isSubmitting = addBuildingMutation.isLoading || updateBuildingMutation.isLoading;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const campusLayoutForNew = () => {
    const index = existingCount;
    const cols = Math.min(4, Math.max(1, Math.ceil(Math.sqrt(existingCount + 1))));
    const col = index % cols;
    const row = Math.floor(index / cols);
    return {
      x: 48 + col * 200,
      y: 56 + row * 180,
      width: 140,
      height: 120,
      rotation: 0,
    };
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      totalFloors: Number(formData.totalFloors),
      buildingCode: formData.buildingCode,
      address: formData.address,
      constructionYear: formData.constructionYear,
      architect: formData.architect,
      description: formData.description,
      status: formData.status,
      fireSafetyStatus: formData.fireSafetyStatus,
      legalClearance: formData.legalClearance,
      emergencyExits: Number(formData.emergencyExits),
      totalCapacity: Number(formData.totalCapacity),
      internetAvailability: formData.internetAvailability,
      powerBackup: formData.powerBackup,
      washrooms: Number(formData.washrooms),
      elevators: Number(formData.elevators),
      parkingCapacity: Number(formData.parkingCapacity),
    };

    try {
      if (isUpdating) {
        await updateBuildingMutation.mutateAsync({ buildingId: building._id, data: payload });
      } else {
        await addBuildingMutation.mutateAsync({
          branchId,
          data: { ...payload, campusLayout: campusLayoutForNew() },
        });
      }
      onClose();
    } catch (error) {
      NotifyError(error?.response?.data?.message || 'Failed to save building', error);
    }
  };

  return (
    <ModalX
      isOpen={isOpen}
      onClose={() => !isSubmitting && onClose()}
      title={isUpdating ? 'Update Building' : 'Add Building'}
      maxWidth="xxl"
    >
      <form onSubmit={handleFormSubmit}>
        <div className="space-y-4">
          <div className="flex gap-4 flex-col md:flex-row">
            <Input label="Name" name="name" value={formData.name} onChange={handleInputChange} required disabled={isSubmitting} />
            <Input label="Building Code" name="buildingCode" value={formData.buildingCode} onChange={handleInputChange} required disabled={isSubmitting} />
          </div>
          <Input label="Address" name="address" value={formData.address} onChange={handleInputChange} required disabled={isSubmitting} />
          <div className="flex gap-4 flex-col md:flex-row">
            <Input label="Total Floors" name="totalFloors" type="number" value={formData.totalFloors} onChange={handleInputChange} required disabled={isSubmitting} />
            <Select
              label="Status"
              value={formData.status}
              onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
              disabled={isSubmitting}
            >
              <Option value="operational">Operational</Option>
              <Option value="in_maintenance">In Maintenance</Option>
              <Option value="under_construction">Under Construction</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </div>
          <div className="flex gap-4 flex-col md:flex-row">
            <Input label="Construction Year" name="constructionYear" value={formData.constructionYear} onChange={handleInputChange} disabled={isSubmitting} />
            <Input label="Architect" name="architect" value={formData.architect} onChange={handleInputChange} disabled={isSubmitting} />
          </div>
          <Textarea label="Description" name="description" value={formData.description} onChange={handleInputChange} disabled={isSubmitting} />
          <div className="flex gap-4 flex-wrap">
            <Checkbox label="Fire Safety" name="fireSafetyStatus" checked={formData.fireSafetyStatus} onChange={handleCheckBoxChange} disabled={isSubmitting} />
            <Checkbox label="Legal Clearance" name="legalClearance" checked={formData.legalClearance} onChange={handleCheckBoxChange} disabled={isSubmitting} />
            <Checkbox label="Internet" name="internetAvailability" checked={formData.internetAvailability} onChange={handleCheckBoxChange} disabled={isSubmitting} />
            <Checkbox label="Power Backup" name="powerBackup" checked={formData.powerBackup} onChange={handleCheckBoxChange} disabled={isSubmitting} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="text" className="rounded-full" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" className="rounded-full" disabled={isSubmitting}>
              {isSubmitting ? <Spinner className="h-4 w-4" /> : (isUpdating ? 'Update' : 'Create')}
            </Button>
          </div>
        </div>
      </form>
    </ModalX>
  );
};

export default BuildingCrudModal;
